import { useEffect } from 'react';
import { programs } from '../../../mocks/programs';

type Program = typeof programs[0];

interface ProgramModalProps {
  program: Program;
  onClose: () => void;
}

export default function ProgramModal({ program, onClose }: ProgramModalProps) {
  const { curriculum } = program;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl"
        style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className="relative h-52 overflow-hidden rounded-t-2xl">
          <img
            src={program.image}
            alt={program.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          <div className="absolute bottom-5 left-6">
            <span className="text-red-400 text-xs font-semibold tracking-widest uppercase block mb-1">{program.label}</span>
            <h2 className="font-serif-kr font-black text-white text-3xl">{program.title}</h2>
            <p className="text-neutral-400 text-sm mt-0.5">{program.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Info Row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: 'ri-user-line', label: '대상', value: curriculum.target },
              { icon: 'ri-calendar-line', label: '수업일정', value: curriculum.schedule },
              { icon: 'ri-time-line', label: '기간', value: curriculum.duration },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <i className={`${item.icon} text-red-500 text-base block mb-1`} />
                <div className="text-neutral-500 text-[10px] mb-0.5">{item.label}</div>
                <div className="text-white text-xs font-semibold leading-snug">{item.value}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          <div>
            <p className="text-neutral-400 text-sm leading-relaxed">{program.description}</p>
          </div>

          {/* Curriculum Steps */}
          <div>
            <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
              <i className="ri-book-open-line text-red-500" />
              커리큘럼
            </h3>
            <div className="space-y-3">
              {curriculum.steps.map((s, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div
                    className="flex-shrink-0 w-16 text-center px-2 py-1 rounded-lg text-[10px] font-bold"
                    style={{ background: 'rgba(225,29,72,0.15)', color: '#f87171' }}
                  >
                    {s.step}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="text-white text-sm font-semibold">{s.title}</div>
                    <div className="text-neutral-500 text-xs mt-0.5 leading-relaxed">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Curriculum Images */}
          {curriculum.curriculumImages.length > 0 && (
            <div>
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-image-line text-red-500" />
                커리큘럼 타임라인
              </h3>
              <div className="flex flex-col gap-3">
                {curriculum.curriculumImages.map((img: string, i: number) => (
                  <div key={i} className="w-full rounded-xl overflow-hidden">
                    <img
                      src={img}
                      alt={`커리큘럼 ${i + 1}`}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              onClose();
              setTimeout(() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }, 300);
            }}
            className="block w-full py-3 rounded-full bg-red-700 text-white text-sm font-semibold text-center hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            입시/입학상담 신청 <i className="ri-arrow-right-line ml-1" />
          </a>
        </div>
      </div>
    </div>
  );
}
